import { AccountPayableModel } from 'api-hooks/account-payable';
import { AccountReceivableModel } from 'api-hooks/account-receivable';
import { ApprovalableTypeEnum } from 'api-hooks/common';
import { OrderAdjustmentModel } from 'api-hooks/order-adjustment';
import { ReceiptModel } from 'api-hooks/receipt';
import { BalanceAdjustmentModel } from 'api-hooks/transactions/balance-adjustment';
import { BankTransactionModel } from 'api-hooks/transactions/bank-transaction';
import { CashTransactionModel } from 'api-hooks/transactions/cash-transaction';
import { IncomeCostModel } from 'api-hooks/transactions/income-cost';
import { TransferModel } from 'api-hooks/transactions/transfer';
import isEmpty from 'lodash/isEmpty';

export default function defaultValuesTransaction(
  data:
    | IncomeCostModel
    | BalanceAdjustmentModel
    | BankTransactionModel
    | CashTransactionModel
    | AccountPayableModel
    | AccountReceivableModel
    | TransferModel
    | ReceiptModel
    | OrderAdjustmentModel,
  approvalableType: string,
  t: any,
) {
  const defaultValues: any = {
    number: data.number,
    description: data.description,
    status: data.status,
    id: data.id,
  };

  if (approvalableType === ApprovalableTypeEnum.BankTransaction) {
    const temp = data as BankTransactionModel;
    return {
      ...defaultValues,
      transactionAt: temp.transactionAt,
      type: temp.type,
      bankAccountId: temp.bankAccount.id,
      bankAccountName: `${temp.bankAccount.name} (${temp.bankAccount.accountNumber})`,
      amount: temp.amount,
    };
  }

  if (approvalableType === ApprovalableTypeEnum.CashTransaction) {
    const temp = data as CashTransactionModel;
    return {
      ...defaultValues,
      transactionAt: temp.transactionAt,
      type: temp.type,
      cashAccountId: temp.cashAccount.id,
      cashAccountName: `${temp.cashAccount.name} (${temp.cashAccount.code})`,
      amount: temp.amount,
    };
  }

  if (approvalableType === ApprovalableTypeEnum.BalanceAdjustment) {
    const temp = data as BalanceAdjustmentModel;
    return {
      ...defaultValues,
      balanceableId: temp.balanceable.id,
      balanceableName: `${
        temp.balanceableType === 'Customer' ? temp.balanceable.title || '' : ''
      }${temp.balanceable.name}${
        temp.balanceable.alias ? ` - ${temp.balanceable.alias}` : ''
      }`,
      type: temp.type,
      amount: temp.amount,
      balanceableType: temp.balanceableType,
      balance: temp.balance,
      date: temp.date,
    };
  }

  if (approvalableType === ApprovalableTypeEnum.Transfer) {
    const temp = data as TransferModel;
    return {
      ...defaultValues,
      transactionAt: temp.transactionAt,
      amount: temp.amount,
      fromAccountType: temp.fromAccountType,
      fromAccountable: temp.fromAccountable.id,
      fromAccountableName: `${temp.fromAccountable.name} (${
        temp.fromAccountable.accountNumber || temp.fromAccountable.code
      })`,
      toAccountType: temp.toAccountType,
      toAccountable: temp.toAccountable.id,
      toAccountableName: `${temp.toAccountable.name} (${
        temp.toAccountable.accountNumber || temp.toAccountable.code
      })`,
    };
  }

  if (approvalableType === ApprovalableTypeEnum.IncomeAndCost) {
    const temp = data as IncomeCostModel;
    return {
      ...defaultValues,
      isStatement: temp.isStatement,
      date: temp.date,
      total: temp.total,
      accountableType: temp.accountableType,
      accountableId: temp.accountable.id,
      accountableName: `${temp.accountable.name} (${temp.accountable.accountNumber})`,
    };
  }

  if (approvalableType === ApprovalableTypeEnum.AccountPayable) {
    const temp = data as AccountPayableModel;
    const paymentable = temp.payment.paymentable;
    const chequeNote = temp.payment.chequeNote;
    return {
      ...defaultValues,
      transactionAt: temp.transactionAt,
      agent: temp.agent.name,
      totalPayment: temp.payment.paymentItems.reduce(
        (prev, curr) => prev + curr.amount,
        0,
      ),
      paymentMethod: t(`common:${temp.payment.paymentMethod.type}`),
      paymentable:
        paymentable && !isEmpty(paymentable)
          ? `${paymentable.code} - ${paymentable.accountNumber}`
          : chequeNote
          ? `${chequeNote.bankAccount.name} - ${chequeNote.bankAccount.accountNumber}`
          : '',
    };
  }

  if (approvalableType === ApprovalableTypeEnum.OrderAdjustment) {
    const orderAdjustment = data as OrderAdjustmentModel;

    return {
      ...defaultValues,
      order: `${orderAdjustment.purchase.number} - ${orderAdjustment.purchase.code} - ${orderAdjustment.purchase.agent}`,
      description: orderAdjustment.description,
      amount: orderAdjustment.amount,
    };
  }

  if (approvalableType === ApprovalableTypeEnum.AccountReceivable) {
    const temp = data as AccountReceivableModel;
    const paymentable = temp.payment.paymentable;
    const chequeNote = temp.payment.chequeNote;
    return {
      ...defaultValues,
      transactionAt: temp.transactionAt,
      customer: temp.customer.name,
      totalPayment: temp.payment.paymentItems.reduce(
        (prev, curr) => prev + curr.amount,
        0,
      ),
      paymentMethod: t(`common:${temp.payment.paymentMethod.type}`),
      paymentable:
        paymentable && !isEmpty(paymentable)
          ? `${paymentable.code} - ${paymentable.accountNumber}`
          : chequeNote
          ? `${chequeNote.bankAccount.name} - ${chequeNote.bankAccount.accountNumber}`
          : '',
    };
  }

  if (approvalableType === ApprovalableTypeEnum.Receipt) {
    const temp = data as ReceiptModel;

    return {
      ...defaultValues,
      customer: `${temp.customer.name}${
        temp.customer.alias ? ` (${temp.customer.alias})` : ''
      } - ${temp.customer.phone}`,
      billingTo: temp.customerBilling?.name ?? '',
      receiptDetails: temp.receiptDetails.map((detail) => ({
        isCheck: true,
        dueDate: detail.sale.dueAt,
        invoiceNumber: detail.sale.invoiceNumber,
        status: detail.sale.status,
        saleId: detail.sale.id,
        saleRefId: detail.sale.refId,
        total: detail.sale.total,
        paidAmount: detail.sale.paidAmount,
      })),
      transactionAt: temp.transactionAt,
    };
  }

  return defaultValues;
}
